import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useAsyncTaskStream } from "../../hooks/useAsyncTaskStream";

import pubSub from "../../utils/PubSub";

type FeedbackTaskWatcherProps = {
	taskId: string;
	queryKeys: QueryKey[];
	topics?: string[];
	onDone: (taskId: string) => void;
};

/**
 * Invisible watcher for one enqueued bulk-feedback task. The bulk endpoints return on
 * enqueue (202), so invalidating the feedback caches at mutation time can refetch before
 * the worker writes the row. This streams the task status and invalidates `queryKeys`
 * once it reaches a terminal state, then unmounts via `onDone`. If the stream dies
 * first — retries exhausted, or a clean close without a terminal update (the server
 * ends the response on Redis errors) — it invalidates anyway rather than staying stale.
 */
export const FeedbackTaskWatcher = ({ taskId, queryKeys, topics, onDone }: FeedbackTaskWatcherProps): null => {
	const client = useQueryClient();
	// Terminal-or-die: bound retries so a stalled stream eventually gives up and we invalidate caches
	// instead of tracking forever (other consumers keep the default keep-retrying behavior).
	const { task, error, isConnected, isRetrying, hasConnected } = useAsyncTaskStream(taskId, {
		boundRetriesOnStalledProgress: true,
	});
	const doneRef = useRef(false);

	useEffect(() => {
		if (doneRef.current) return;
		const isTerminal = task?.status === "completed" || task?.status === "failed";
		// Stream dead with no terminal update coming: retries exhausted (error) or a clean EOF after
		// connecting. `hasConnected` comes from the hook (monotonic, batch-safe) so a fast clean close
		// can't slip through with the watcher never having observed the transient `isConnected` true.
		const streamDied = !isConnected && !isRetrying && (error !== null || hasConnected);
		if (!isTerminal && !streamDied) return;
		doneRef.current = true;
		queryKeys.forEach((queryKey) => client.invalidateQueries({ queryKey }));
		topics?.forEach((topic) => pubSub.publish(topic));
		onDone(taskId);
	}, [task, error, isConnected, isRetrying, hasConnected, taskId, queryKeys, topics, client, onDone]);

	return null;
};
